// ABOUTME: Context distilling the router state the edit-node form relies on (GL-creation flow,
// ABOUTME: prototype GL param, parent model) so the form tree does not subscribe to every navigation.
import { useStableCallback } from '@src/_new/hooks/useStableCallback';
import { createContext, ReactNode, useContext, useMemo, useRef } from 'react';
import {
    NavigateFunction,
    NavigateOptions,
    To,
    useLocation,
    useNavigate,
    useSearchParams,
} from 'react-router-dom';

/** location.state carried by the navigation that follows a GL created from a model link field. */
export type GlCreationRouteState = {
    clickType: 'glCreation';
    glId?: string;
    modelId?: string;
    firstStep?: boolean;
    linkFieldId?: string | null;
};

export type EditNodeRouteContextValue = {
    /**
     * The live location.state of a GL-creation navigation, null for any other navigation. It is
     * the router's own object on purpose: the GL flow marks its steps as handled by deleting
     * fields from it, and readers must see those deletions without a re-render.
     */
    glCreation: GlCreationRouteState | null;
    /** location.state.modelId: the parent to create under when the route carries one. */
    modelId?: string;
    /** The fromPrototypeGlId search param, null when absent. */
    fromPrototypeGlId: string | null;
    /** Clears the search params, only when there are some. */
    clearSearchParams: () => void;
    /** Stable navigate: react-router's own hook re-renders its caller on every navigation. */
    navigate: NavigateFunction;
    /**
     * The form is hosted by the hierarchies page (`/hierarchies/...`), the only place where
     * leaving the form to open another node is a navigation the host can follow. Elsewhere
     * (custom pages, CPQ) the form is a guest of a page that owns the route.
     */
    isHierarchyRoute: boolean;
};

const noRoute: EditNodeRouteContextValue = {
    glCreation: null,
    fromPrototypeGlId: null,
    clearSearchParams: () => {},
    navigate: () => {},
    isHierarchyRoute: false,
};

export const EditNodeRouteContext = createContext<EditNodeRouteContextValue>(noRoute);

export const useEditNodeRoute = () => useContext(EditNodeRouteContext);

/**
 * Reads the router once, at the root of the route tree, and hands the form only the values it
 * relies on. The value keeps its identity across ordinary navigations, so consumers are not
 * re-rendered by them; it changes only when a GL-creation navigation or the prototype GL param
 * comes and goes.
 */
export const EditNodeRouteProvider = ({ children }: { children: ReactNode }) => {
    const { pathname, state } = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const routerNavigate = useNavigate();

    // Only clear search params when there are some: an unconditional navigation here wipes
    // the location state that a just-submitted create navigation set (e.g. the edgeIcon
    // clickType used to select the newly created node), preventing the view from switching
    // to the created node.
    const clearSearchParams = useStableCallback(() => {
        if (searchParams.toString()) {
            setSearchParams(new URLSearchParams());
        }
    });
    const routerNavigateRef = useRef(routerNavigate);
    routerNavigateRef.current = routerNavigate;
    const navigate = useMemo<NavigateFunction>(() => {
        const stableNavigate: NavigateFunction = (
            toOrDelta: To | number,
            options?: NavigateOptions,
        ) =>
            typeof toOrDelta === 'number'
                ? routerNavigateRef.current(toOrDelta)
                : routerNavigateRef.current(toOrDelta, options);
        return stableNavigate;
    }, []);

    const glCreation: GlCreationRouteState | null =
        state?.clickType === 'glCreation' ? (state as GlCreationRouteState) : null;
    const modelId: string | undefined = state?.modelId;
    const fromPrototypeGlId = searchParams.get('fromPrototypeGlId');
    // A boolean, so moving between two nodes of the hierarchies page keeps the value identical.
    const isHierarchyRoute = pathname === '/hierarchies' || pathname.startsWith('/hierarchies/');

    const value = useMemo<EditNodeRouteContextValue>(
        () => ({
            glCreation,
            modelId,
            fromPrototypeGlId,
            clearSearchParams,
            navigate,
            isHierarchyRoute,
        }),
        [glCreation, modelId, fromPrototypeGlId, clearSearchParams, navigate, isHierarchyRoute],
    );

    return <EditNodeRouteContext.Provider value={value}>{children}</EditNodeRouteContext.Provider>;
};
